import React, { useEffect, useState } from 'react';
import {
  Box, TextField, Typography,
  CircularProgress, Alert, InputAdornment, IconButton,
} from '@mui/material';
import { keyframes } from '@mui/material/styles';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../reduxStore/Store';
import { loginUser, clearAuthError } from '../reduxStore/AuthSlice';
import { SESSION_EXPIRED_FLAG } from '../utils/authActions';
import { labels } from '../constants/labels';
import Button from '@mui/material/Button';

/* ─── Contenu éditorial du panneau de marque ─────────────────────────────── */
/** Slogan : la partie « accent » est soulignée à l'apparition. */
const PITCH_TITLE_LEAD = 'Pilotez';
const PITCH_TITLE_ACCENT = 'votre entreprise.';
const PITCH_SUBTITLE =
  `${labels.documentVentePluriel}, stock, ${labels.reglementPluriel.toLowerCase()} et rapports — réunis dans un seul outil.`;
const PITCH_FEATURES = [
  'Rapports PDF / Excel générés à la demande',
  'Multi-département et multi-dépôt',
  'Application mobile iOS & Android',
];

/* ─── Animation d'entrée (logo + slogan) ────────────────────────────────── */
// Jouée une fois au chargement de la page ; coupée si l'utilisateur a demandé
// moins d'animations (media query sur le conteneur racine).
const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const inUp = keyframes`from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; }`;
const inLeft = keyframes`from { opacity: 0; transform: translateX(-14px); } to { opacity: 1; transform: none; }`;
const tileIn = keyframes`
  0%   { opacity: 0; transform: scale(.4) rotate(-10deg); }
  70%  { opacity: 1; transform: scale(1.06) rotate(0); }
  100% { opacity: 1; transform: scale(1) rotate(0); }
`;
const drawG = keyframes`from { stroke-dashoffset: 200; } to { stroke-dashoffset: 0; }`;
const halo = keyframes`
  0%   { opacity: 0; transform: scale(1); }
  15%  { opacity: .7; }
  100% { opacity: 0; transform: scale(1.9); }
`;
const wordUp = keyframes`from { transform: translateY(110%); } to { transform: none; }`;
const sweep = keyframes`from { transform: scaleX(0); } to { transform: scaleX(1); }`;

/** Valeur CSS `animation` d'une entrée : durée et délai en secondes, état final conservé. */
const enter = (
  name: ReturnType<typeof keyframes>,
  duration: number,
  delay: number,
  easing = 'cubic-bezier(.2,.7,.2,1)',
) => `${name} ${duration}s ${easing} ${delay}s both`;

/** Délai d'apparition du i-ème mot du slogan. */
const wordDelay = (i: number) => 0.85 + i * 0.12;

const leadWords = PITCH_TITLE_LEAD.split(' ');
const accentWords = PITCH_TITLE_ACCENT.split(' ');
/** Le soulignement part une fois le dernier mot arrivé ; le reste du panneau suit. */
const underlineDelay = wordDelay(leadWords.length + accentWords.length - 1) + 0.36;

/** Mot du slogan qui monte depuis un masque. */
const RevealWord: React.FC<{ delay: number; children: React.ReactNode }> = ({ delay, children }) => (
  <Box component="span" sx={{ display: 'inline-block', overflow: 'hidden', verticalAlign: 'top', pb: '0.08em', mb: '-0.08em' }}>
    <Box component="span" sx={{ display: 'inline-block', animation: enter(wordUp, 0.6, delay, 'cubic-bezier(.2,.8,.2,1)') }}>
      {children}
    </Box>
  </Box>
);

/* ─── Logo ──────────────────────────────────────────────────────────────── */
/** Monogramme : carré arrondi + « G » tracé (même tracé que l'icône de l'app).
 *  Tuile blanche / « G » bleu ici pour ressortir sur le panneau bleu foncé
 *  (le mobile fait l'inverse : carré primaire + « G » blanc, sur fond clair).
 *  `animated` : le carré apparaît, le « G » se dessine puis un halo s'en échappe. */
const LogoMark: React.FC<{ size?: number; animated?: boolean }> = ({ size = 44, animated = false }) => {
  const radius = Math.round(size * 0.28);
  return (
    <Box
      sx={{
        position: 'relative',
        width: size, height: size, flexShrink: 0,
        borderRadius: `${radius}px`,
        bgcolor: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        animation: animated ? enter(tileIn, 0.7, 0.1) : undefined,
        '& path': {
          strokeDasharray: 200,
          animation: animated ? enter(drawG, 0.9, 0.35, 'ease-in-out') : undefined,
        },
      }}
    >
      <svg width={size * 0.68} height={size * 0.68} viewBox="0 0 100 100" aria-hidden focusable="false">
        <path d="M71.2 28.8A30 30 0 1 0 80 50H54" fill="none" stroke="#0d47a1" strokeWidth={12} />
      </svg>
      {animated && (
        <Box
          aria-hidden
          sx={{
            position: 'absolute', inset: '-6px',
            border: '2px solid rgba(255,255,255,0.75)',
            borderRadius: `${radius + 4}px`,
            opacity: 0,
            animation: enter(halo, 0.9, 1.05, 'ease-out'),
          }}
        />
      )}
    </Box>
  );
};

/* ─── Badges stores ─────────────────────────────────────────────────────── */
/** Logo Google Play — triangle « play » quadricolore officiel */
const GooglePlayGlyph: React.FC = () => (
  <svg width="22" height="24" viewBox="0 0 44 48" aria-hidden focusable="false">
    <path d="M3 3 L3 24 L24 24 Z" fill="#00A0FF" />
    <path d="M3 24 L3 45 L24 24 Z" fill="#00E676" />
    <path d="M3 3 L24 24 L40 24 Z" fill="#FFC400" />
    <path d="M3 45 L24 24 L40 24 Z" fill="#FF3A44" />
  </svg>
);

/** Logo Apple — pomme blanche officielle */
const AppleGlyph: React.FC = () => (
  <svg width="20" height="24" viewBox="0 0 384 512" aria-hidden focusable="false">
    <path
      fill="#fff"
      d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"
    />
  </svg>
);

/** Badge de téléchargement style store (Google Play / App Store) */
const StoreBadge: React.FC<{
  href: string;
  icon: React.ReactNode;
  top: string;
  bottom: string;
}> = ({ href, icon, top, bottom }) => (
  <Box
    component="a"
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.25,
      px: 1.75,
      py: 1,
      minWidth: 168,
      height: 50,
      borderRadius: 2,
      bgcolor: '#000',
      color: '#fff',
      border: '1px solid rgba(255,255,255,0.16)',
      textDecoration: 'none',
      boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
      transition: 'transform .15s ease, box-shadow .15s ease, background-color .15s ease',
      '&:hover': {
        bgcolor: '#141414',
        transform: 'translateY(-2px)',
        boxShadow: '0 6px 16px rgba(0,0,0,0.32)',
      },
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center' }}>{icon}</Box>
    <Box sx={{ textAlign: 'left', lineHeight: 1.2 }}>
      <Typography sx={{ fontSize: 10, letterSpacing: 0.3 }}>{top}</Typography>
      <Typography sx={{ fontSize: 15, fontWeight: 600, mt: '-1px' }}>{bottom}</Typography>
    </Box>
  </Box>
);

const StoreBadges: React.FC<{ dark?: boolean }> = ({ dark }) => (
  <Box>
    <Typography
      sx={{ fontSize: 12, mb: 1, color: dark ? 'rgba(255,255,255,0.7)' : 'text.secondary' }}
    >
      Téléchargez l'application mobile
    </Typography>
    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
      <StoreBadge
        href="https://play.google.com/store/apps/details?id=com.agencemobile&pcampaignid=web_share"
        icon={<GooglePlayGlyph />}
        top="DISPONIBLE SUR"
        bottom="Google Play"
      />
      <StoreBadge
        href="https://apps.apple.com/ci/app/gsofterp/id6807514084"
        icon={<AppleGlyph />}
        top="Télécharger dans l'"
        bottom="App Store"
      />
    </Box>
  </Box>
);

/* ─── Page ──────────────────────────────────────────────────────────────── */
const LoginPage: React.FC = () => {
  const dispatch   = useDispatch<AppDispatch>();
  const navigate   = useNavigate();
  const { loading, error, isAuthenticated } = useSelector((state: RootState) => state.AuthSlice);

  const [login,         setLogin]         = useState('');
  const [password,      setPassword]      = useState('');
  const [showPwd,       setShowPwd]       = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  // Bannière "session expirée" si on a été déconnecté par l'intercepteur 401
  useEffect(() => {
    if (sessionStorage.getItem(SESSION_EXPIRED_FLAG)) {
      setSessionExpired(true);
      sessionStorage.removeItem(SESSION_EXPIRED_FLAG);
    }
  }, []);

  // Redirection si déjà authentifié
  useEffect(() => {
    if (isAuthenticated) navigate('/', { replace: true });
  }, [isAuthenticated, navigate]);

  // Nettoyage de l'erreur quand l'utilisateur retape
  useEffect(() => {
    if (error) dispatch(clearAuthError());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [login, password]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!login.trim() || !password) return;
    dispatch(loginUser({ login: login.trim(), password }));
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        bgcolor: '#f7f9fc',
        '@media (prefers-reduced-motion: reduce)': {
          '& *': { animation: 'none !important' },
        },
      }}
    >
      {/* ============ Panneau de marque ============ */}
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          color: '#fff',
          background: 'linear-gradient(158deg, #1565c0 0%, #0d47a1 62%, #0b3c88 100%)',
          width: { xs: '100%', md: 460, lg: 560 },
          flexShrink: 0,
          px: { xs: 3, md: 7 },
          py: { xs: 4, md: 8 },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: { xs: 'flex-start', md: 'space-between' },
          gap: { xs: 2.5, md: 0 },
        }}
      >
        {/* motif décoratif — bureau uniquement */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute', right: -170, bottom: -170, width: 520, height: 520,
            display: { xs: 'none', md: 'block' }, pointerEvents: 'none',
          }}
        >
          {[255, 205, 150, 95].map((r) => (
            <Box
              key={r}
              sx={{
                position: 'absolute', top: '50%', left: '50%',
                width: r * 2, height: r * 2, mt: `${-r}px`, ml: `${-r}px`,
                border: '2px solid rgba(255,255,255,0.12)', borderRadius: '50%',
              }}
            />
          ))}
        </Box>

        {/* logo */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, position: 'relative', zIndex: 1 }}>
          <LogoMark size={44} animated />
          <Typography sx={{ fontSize: 20, fontWeight: 700, letterSpacing: '0.01em', animation: enter(inLeft, 0.5, 0.55) }}>
            Gsoft Web ERP
          </Typography>
        </Box>

        {/* accroche */}
        <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 470 }}>
          <Typography
            sx={{
              fontSize: { xs: 28, md: 40 }, fontWeight: 700, lineHeight: 1.15,
            }}
          >
            {leadWords.map((word, i) => (
              <React.Fragment key={`lead-${i}`}>
                <RevealWord delay={wordDelay(i)}>{word}</RevealWord>{' '}
              </React.Fragment>
            ))}
            <Box component="span" sx={{ position: 'relative', display: 'inline-block', pb: { xs: '5px', md: '6px' } }}>
              {accentWords.map((word, i) => (
                <React.Fragment key={`accent-${i}`}>
                  {i > 0 && ' '}
                  <RevealWord delay={wordDelay(leadWords.length + i)}>{word}</RevealWord>
                </React.Fragment>
              ))}
              {/* soulignement balayé une fois le slogan affiché */}
              <Box
                component="span"
                aria-hidden
                sx={{
                  position: 'absolute', left: 0, right: '6px', bottom: 0,
                  height: { xs: '4px', md: '5px' }, borderRadius: '3px',
                  bgcolor: '#90caf9', transformOrigin: '0 50%',
                  animation: enter(sweep, 0.6, underlineDelay, 'cubic-bezier(.6,0,.2,1)'),
                }}
              />
            </Box>
          </Typography>
          <Typography
            sx={{
              mt: { xs: 1, md: 2.25 },
              fontSize: { xs: 14, md: 16 }, lineHeight: 1.6,
              color: 'rgba(255,255,255,0.82)',
              animation: enter(inUp, 0.5, underlineDelay + 0.1),
            }}
          >
            {PITCH_SUBTITLE}
          </Typography>

          <Box sx={{ display: { xs: 'none', md: 'flex' }, flexDirection: 'column', gap: 1.75, mt: 4 }}>
            {PITCH_FEATURES.map((feature, i) => (
              <Box
                key={feature}
                sx={{ display: 'flex', alignItems: 'center', gap: 1.5, animation: enter(inUp, 0.45, underlineDelay + 0.3 + i * 0.1) }}
              >
                <Box
                  sx={{
                    width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                    bgcolor: 'rgba(255,255,255,0.16)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  <CheckRoundedIcon sx={{ fontSize: 15 }} />
                </Box>
                <Typography sx={{ fontSize: 15, color: 'rgba(255,255,255,0.92)' }}>{feature}</Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* badges stores — bureau, dans le panneau */}
        <Box
          sx={{
            position: 'relative', zIndex: 1, display: { xs: 'none', md: 'block' },
            animation: enter(fadeIn, 0.5, underlineDelay + 0.65, 'ease'),
          }}
        >
          <StoreBadges dark />
        </Box>
      </Box>

      {/* ============ Panneau formulaire ============ */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 3,
          py: { xs: 5, md: 6 },
          animation: enter(fadeIn, 0.4, 0.05, 'ease'),
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 384 }}>
          <Typography sx={{ fontSize: 28, fontWeight: 700 }}>Bienvenue</Typography>
          <Typography sx={{ fontSize: 14, color: 'text.secondary', mt: 1 }}>
            Connectez-vous à votre espace Gsoft.
          </Typography>

          {/* Bannière session expirée — affichée après une déconnexion forcée 401 */}
          {sessionExpired && !error && (
            <Alert severity="warning" sx={{ mt: 3 }} onClose={() => setSessionExpired(false)}>
              Session expirée — veuillez vous reconnecter.
            </Alert>
          )}

          {/* Erreur */}
          {error && (
            <Alert severity="error" sx={{ mt: 3 }}>
              {error}
            </Alert>
          )}

          {/* Formulaire */}
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3.5 }}>
            <TextField
              label="Identifiant"
              value={login}
              onChange={(e) => setLogin(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
              autoComplete="username"
              autoFocus
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonOutlineIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Mot de passe"
              type={showPwd ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              sx={{ mb: 1 }}
              autoComplete="current-password"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowPwd((v) => !v)} edge="end">
                      {showPwd ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2.5 }}>
              <Typography variant="caption">
                <Link to="/forgot-password" style={{ color: '#1976d2', fontWeight: 500, textDecoration: 'none' }}>
                  Mot de passe oublié ?
                </Link>
              </Typography>
            </Box>

            <Button
              variant="contained"
              type="submit"
              fullWidth
              size="large"
              disabled={loading || !login.trim() || !password}
            >
              {loading ? <CircularProgress size={22} color="inherit" /> : 'Se connecter'}
            </Button>
          </Box>

          {/* Création de compte */}
          <Typography sx={{ textAlign: 'center', fontSize: 13, color: 'text.secondary', mt: 3 }}>
            Pas encore de compte ?{' '}
            <Link to="/register" style={{ color: '#1976d2', fontWeight: 600, textDecoration: 'none' }}>
              Créer un compte
            </Link>
          </Typography>

          {/* badges stores — mobile, sous le formulaire */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'center', textAlign: 'center', mt: 4 }}>
            <StoreBadges />
          </Box>

          {/* Politique de confidentialité (page statique servie par le front) */}
          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Typography variant="caption" color="text.secondary">
              <a
                href="/privacy.html"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'inherit', textDecoration: 'underline' }}
              >
                Politique de confidentialité
              </a>
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LoginPage;
